import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { categories, getComicsByCategory } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    setActiveCategory(slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryName = currentCategory?.name || '全部';
  const filteredComics = getComicsByCategory(slug);

  const sortedComics = [...filteredComics].sort((a, b) => {
    if (sortBy === 'popular') return b.views - a.views;
    if (sortBy === 'rating') return b.rating - a.rating;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  return (
    <>
      <SEOHead
        title={`${categoryName}漫画 - 51漫画在线观看 | 免费${categoryName}漫画全集在线阅读`}
        description={`51漫画${categoryName}漫画分类，提供免费${categoryName}漫画全集在线阅读、下拉式阅读。海量${categoryName}漫画资源，每日更新，支持网页观看与在线漫画阅读。`}
        keywords={`${categoryName}漫画,51漫画,免费漫画,在线漫画,下拉式漫画,网页观看`}
        canonicalUrl={`https://www.51manga.com/category/${slug}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '分类浏览', href: '/category/all' },
                  { label: `${categoryName}漫画` },
                ]}
              />
            </div>

            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              {categoryName === '全部' ? '全部分类漫画' : `${categoryName}漫画`}
            </h1>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-900">{filteredComics.length}</strong> 部漫画
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {([
                  { key: 'latest', label: '最新' },
                  { key: 'popular', label: '人气' },
                  { key: 'rating', label: '评分' },
                ] as const).map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === opt.key
                        ? 'bg-white text-foreground-900 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {sortedComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {sortedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="medium" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">该分类暂无漫画，看看其他分类吧~</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}